import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Button,
	Flex,
	Input,
	InputNumber,
	InputRef,
	Select,
	Space,
	Tag,
	Tooltip,
	Typography,
} from 'antd';
import { CheckOutlined, CloseOutlined, OpenAIOutlined } from '@ant-design/icons';
import { Balloon, ToleranceSource } from '../../interfaces/Ballooning';
import BalloonReviewActions from '../BalloonReviewActions';
import NominalInput from '../NominalInput';
import { GageType } from '../../interfaces/Quality.Interface';
import { useGetGageTypesQuery, useGetKeyenceSystemsQuery } from '../../redux/api/quality';
import { isUnreviewedAutoBalloon } from '../../utils/balloonReview';
import {
	isFieldDisabled,
	reachableKeyenceSystems,
	StrategyOption,
	strategyOptions,
	strategyValue,
	toolField,
} from '../BalloonTable/measurementFields';

// Threads are checked with thread gages, whose nominal is the thread designation text.
const NO_NOMINAL_TYPES = ['Engraving', 'Surface Finish', 'Other', 'Thread'];

const TOLERANCE_SOURCE_CAPTIONS: Record<ToleranceSource, string> = {
	drawing: 'Tolerance printed on the drawing',
	table: 'Tolerance from the tolerance table',
	manual: 'Tolerance entered by hand',
};

// Tolerances are numbers, like the table's InputNumber cells write; the nominal is the
// typed text so its trailing zeros survive.
export type BalloonMeasurementUpdate = {
	nominal?: string | null;
	tolerance_plus?: number | null;
	tolerance_minus?: number | null;
	text_value?: string;
};

interface BalloonQuickEditProps {
	balloon: Balloon;
	editable: boolean;
	onChange: (updates: BalloonMeasurementUpdate) => void;
	/** Shows the Strategy picker; runs the table's strategy change (with its confirmations). */
	onStrategyChange?: (value: string, option: StrategyOption) => void;
	/** Shows the Tool picker (Keyence view, hand gage, or visual type). */
	onToolChange?: (value: string) => void;
	/** Limits the Keyence devices offered, as in the table. */
	keyenceBuildingId?: string | null;
	onApprove?: () => Promise<boolean>;
	/** Shows Delete next to Approve in the popup. */
	onDelete?: () => void;
	onClose?: () => void;
	// 'panel' fills its container and leaves closing and the actions to the host.
	variant?: 'popup' | 'panel';
}

const FIELD_LABEL_STYLE: React.CSSProperties = {
	fontSize: 10,
	lineHeight: '14px',
	color: 'rgba(0, 0, 0, 0.45)',
	textTransform: 'uppercase',
	letterSpacing: 0.3,
};

// Values like 0.00500 need the width more than AntD's default side padding does.
const COMPACT_INPUT_CLASS = '[&_.ant-input-number-input]:!px-1';

const toInputValue = (value: string | number | null | undefined) =>
	value === '' || value === null || value === undefined ? null : Number(value);

const BalloonQuickEdit: React.FC<BalloonQuickEditProps> = ({
	balloon,
	editable,
	onChange,
	onStrategyChange,
	onToolChange,
	keyenceBuildingId = null,
	onApprove,
	onDelete,
	onClose,
	variant = 'popup',
}) => {
	const isPanel = variant === 'panel';
	const [approving, setApproving] = useState(false);
	const firstFieldRef = useRef<HTMLInputElement & InputRef>(null);
	const unreviewed = isUnreviewedAutoBalloon(balloon);
	const editsStrategy = !!onStrategyChange || !!onToolChange;

	const { data: gageTypes } = useGetGageTypesQuery({}, { skip: !editsStrategy });
	const { data: allKeyenceSystems } = useGetKeyenceSystemsQuery(undefined, {
		skip: !onStrategyChange,
	});
	const keyenceSystems = useMemo(
		() => reachableKeyenceSystems(allKeyenceSystems, keyenceBuildingId),
		[allKeyenceSystems, keyenceBuildingId]
	);
	const fastlaneGages = useMemo(
		() => (gageTypes ?? []).filter((gage: GageType) => gage.is_fastlane_gage),
		[gageTypes]
	);
	const gageMap = useMemo(
		() => Object.fromEntries(fastlaneGages.map((gage) => [gage.id, gage])),
		[fastlaneGages]
	);

	// With the pickers shown, follow the table's rules exactly; without them (no gage data),
	// the balloon's measurement type decides.
	const showNominal = editsStrategy
		? !isFieldDisabled('nominal', balloon, gageMap)
		: !NO_NOMINAL_TYPES.includes(balloon.measurement_type);
	const showDescription = editsStrategy
		? !showNominal && !isFieldDisabled('text_value', balloon, gageMap)
		: !showNominal &&
			balloon.measurement_type !== 'Dimension' &&
			balloon.measurement_strategy !== 'Keyence';
	const tool = onToolChange ? toolField(balloon, fastlaneGages) : null;

	useEffect(() => {
		if (editable && !isPanel) firstFieldRef.current?.focus();
	}, [balloon.id]);

	const handleApprove = async () => {
		if (!onApprove) return;
		setApproving(true);
		try {
			if (await onApprove()) onClose?.();
		} finally {
			setApproving(false);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		// Keep Backspace/Delete in these inputs from reaching the table's row shortcuts.
		e.stopPropagation();
		// Enter in a picker chooses an option; it must not close the popup too.
		const inPicker = !!(e.target as HTMLElement).closest?.('.ant-select');
		if (!isPanel && (e.key === 'Escape' || (e.key === 'Enter' && !inPicker))) {
			e.preventDefault();
			onClose?.();
		}
	};

	const confidence =
		typeof balloon.ai_confidence === 'number'
			? `${Math.round(balloon.ai_confidence * 100)}% confidence`
			: null;
	const readFromDrawing = balloon.is_auto_generated
		? [balloon.ai_raw_text && `Read from drawing: ${balloon.ai_raw_text}`, confidence]
				.filter(Boolean)
				.join(' · ') || undefined
		: undefined;

	return (
		<div
			onKeyDown={handleKeyDown}
			data-testid="balloon-quick-edit"
			style={{ width: isPanel ? '100%' : 260 }}
		>
			<Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
				<Space size={6}>
					<Typography.Text strong>Balloon {balloon.balloon_number}</Typography.Text>
					{balloon.is_auto_generated && (
						<Tag
							icon={unreviewed ? <OpenAIOutlined /> : <CheckOutlined />}
							color={unreviewed ? 'orange' : 'green'}
							style={{ marginInlineEnd: 0 }}
							data-testid="balloon-quick-edit-review-tag"
						>
							{unreviewed ? 'Needs review' : 'Reviewed'}
						</Tag>
					)}
				</Space>
				{!isPanel && (
					<Button
						size="small"
						type="text"
						icon={<CloseOutlined />}
						onClick={onClose}
						aria-label="Close"
					/>
				)}
			</Flex>

			{editsStrategy ? (
				<Flex gap={4}>
					{onStrategyChange && (
						<Flex vertical style={{ flex: 1, minWidth: 0 }}>
							<span style={FIELD_LABEL_STYLE}>Strategy</span>
							<Select
								size="small"
								style={{ width: '100%' }}
								status={balloon.measurement_strategy ? undefined : 'error'}
								placeholder="Select strategy..."
								value={strategyValue(balloon, keyenceSystems) || undefined}
								options={strategyOptions(keyenceSystems)}
								onChange={(value, option) =>
									onStrategyChange(value, option as StrategyOption)
								}
								disabled={!editable}
								popupMatchSelectWidth={false}
								data-testid="balloon-quick-edit-strategy"
							/>
						</Flex>
					)}
					{tool && (
						<Flex vertical style={{ flex: 1, minWidth: 0 }}>
							<span style={FIELD_LABEL_STYLE}>{tool.label}</span>
							<Select
								size="small"
								style={{ width: '100%' }}
								status={tool.value ? undefined : 'error'}
								placeholder={tool.placeholder}
								value={tool.value || undefined}
								options={tool.options}
								onChange={onToolChange}
								disabled={!editable}
								showSearch
								optionFilterProp="label"
								popupMatchSelectWidth={false}
								data-testid="balloon-quick-edit-tool"
							/>
						</Flex>
					)}
				</Flex>
			) : (
				<Typography.Text type="secondary" style={{ fontSize: 12 }}>
					{[balloon.measurement_strategy, balloon.measurement_type, balloon.view]
						.filter(Boolean)
						.join(' · ') || 'No strategy set'}
				</Typography.Text>
			)}
			{balloon.is_auto_generated && balloon.ai_strategy_reason && (
				<Typography.Paragraph
					type="secondary"
					italic
					style={{ fontSize: 11, marginBottom: 0 }}
					data-testid="balloon-strategy-reason"
				>
					{balloon.ai_strategy_reason}
				</Typography.Paragraph>
			)}

			{showNominal ? (
				<Flex vertical gap={4} style={{ marginTop: 8 }}>
					<Flex gap={4}>
						<Flex vertical style={{ flex: 4, minWidth: 0 }}>
							<span style={FIELD_LABEL_STYLE}>Nominal</span>
							<Tooltip title={readFromDrawing}>
								<div data-testid="balloon-quick-edit-nominal">
									<NominalInput
										ref={firstFieldRef}
										size="small"
										className="!px-1"
										nominal={balloon.nominal}
										decimals={balloon.nominal_decimals}
										disabled={!editable}
										onChange={(nominal) => onChange({ nominal })}
									/>
								</div>
							</Tooltip>
						</Flex>
						<Flex vertical style={{ flex: 3, minWidth: 0 }}>
							<span style={FIELD_LABEL_STYLE}>− Tol</span>
							<InputNumber
								size="small"
								className={COMPACT_INPUT_CLASS}
								style={{ width: '100%' }}
								controls={false}
								value={toInputValue(balloon.tolerance_minus)}
								disabled={!editable}
								onChange={(value) =>
									onChange({ tolerance_minus: value ?? null })
								}
							/>
						</Flex>
						<Flex vertical style={{ flex: 3, minWidth: 0 }}>
							<span style={FIELD_LABEL_STYLE}>+ Tol</span>
							<InputNumber
								size="small"
								className={COMPACT_INPUT_CLASS}
								style={{ width: '100%' }}
								controls={false}
								value={toInputValue(balloon.tolerance_plus)}
								disabled={!editable}
								onChange={(value) =>
									onChange({ tolerance_plus: value ?? null })
								}
							/>
						</Flex>
					</Flex>
					{balloon.tolerance_source && (
						<Typography.Text type="secondary" style={{ fontSize: 12 }}>
							{TOLERANCE_SOURCE_CAPTIONS[balloon.tolerance_source]}
						</Typography.Text>
					)}
				</Flex>
			) : showDescription ? (
				<Flex vertical style={{ marginTop: 8 }}>
					<span style={FIELD_LABEL_STYLE}>
						{balloon.measurement_type === 'Thread' ||
						gageMap[balloon.gage_type]?.category?.name === 'Thread Gages'
							? 'Thread'
							: balloon.measurement_type === 'Engraving'
								? 'Engraving text'
								: 'Description'}
					</span>
					<Tooltip title={readFromDrawing}>
						<div>
							<Input
								ref={firstFieldRef}
								size="small"
								className="!px-1"
								value={balloon.text_value || ''}
								disabled={!editable}
								onChange={(e) => onChange({ text_value: e.target.value })}
							/>
						</div>
					</Tooltip>
				</Flex>
			) : null}

			{!isPanel && unreviewed && onApprove && (
				<div style={{ marginTop: 10 }}>
					<BalloonReviewActions
						balloonNumber={balloon.balloon_number}
						onApprove={handleApprove}
						approving={approving}
						onDelete={
							onDelete &&
							(() => {
								onDelete();
								onClose?.();
							})
						}
						deleteDisabled={!editable}
						testIdPrefix="balloon-quick-edit"
					/>
				</div>
			)}
		</div>
	);
};

export default BalloonQuickEdit;
